/**
 * MaterialReceiveModal — opens from the Receive/View button on a raw
 * material row (Manufacturing > Material Purchases). Mirror of
 * ProductReceiveModal but with a supplier-scoped material list: once
 * the operator picks a supplier, the modal shows ALL raw materials
 * linked to that supplier with their on-hand + a qty input, so a
 * single Manage Purchase can be distributed across multiple materials
 * in one Accept.
 *
 * Accept POSTs `/manufacturing/material-purchases/allocate` which
 * atomically writes one mmp row per non-zero material + one
 * raw_material_stock_movements row per non-zero material. Partial
 * allocations allowed (Σ inputs × 1000 ≤ MP.remaining_millis).
 *
 * View mode (pending_invoice_count = 0): no dropdowns, no Accept;
 * just the material's on-hand display.
 */

import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Field } from '../../components/molecules/Field'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { MaterialQtyTable } from '../../components/molecules/MaterialQtyTable'
import type {
  MaterialAggregateDto,
  MaterialReceiveOptionsDto,
  ReceiveLine,
  SupplierWithInvoicesDto,
  UnusedInvoiceDto
} from '../../../viewmodels/useManufacturingMaterialPurchases'

interface Props {
  readonly open: boolean
  readonly material: MaterialAggregateDto | null
  readonly onClose: () => void
  readonly getOptions: (
    materialId: string
  ) => Promise<{ ok: true; data: MaterialReceiveOptionsDto } | { ok: false; message: string }>
  readonly onAllocate: (
    input: { managePurchaseId: string; lines: ReadonlyArray<ReceiveLine> }
  ) => Promise<{ ok: true; ids: ReadonlyArray<string> } | { ok: false; field: string | null; message: string }>
}

function formatQty(millis: number): string {
  return (millis / 1000).toLocaleString('en-IN', { maximumFractionDigits: 3 })
}

export function MaterialReceiveModal({
  open,
  material,
  onClose,
  getOptions,
  onAllocate
}: Props): JSX.Element | null {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [allSuppliers, setAllSuppliers] = useState<ReadonlyArray<SupplierWithInvoicesDto>>([])

  const [supplierId, setSupplierId] = useState<string>('')
  const [managePurchaseId, setManagePurchaseId] = useState<string>('')
  const [qtyByMaterial, setQtyByMaterial] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!open || material === null) return
    const ac = new AbortController()
    setLoading(true)
    setError(null)
    setAllSuppliers([])
    setSupplierId('')
    setManagePurchaseId('')
    setQtyByMaterial({})
    ;(async () => {
      const r = await getOptions(material.raw_material_id)
      if (ac.signal.aborted) return
      setLoading(false)
      if (!r.ok) {
        setError(r.message)
        return
      }
      setAllSuppliers(r.data.suppliers)
    })().catch(() => { /* aborted */ })
    return () => ac.abort()
  }, [open, material, getOptions])

  // Suppliers with at least one allocatable Manage Purchase.
  const eligibleSuppliers = useMemo(
    () => allSuppliers.filter((s) => s.invoices.length > 0),
    [allSuppliers]
  )
  const supplier = useMemo<SupplierWithInvoicesDto | null>(
    () => eligibleSuppliers.find((s) => s.supplier_id === supplierId) ?? null,
    [eligibleSuppliers, supplierId]
  )
  const invoice = useMemo<UnusedInvoiceDto | null>(
    () => supplier?.invoices.find((i) => i.purchase_id === managePurchaseId) ?? null,
    [supplier, managePurchaseId]
  )
  const supplierMaterials = useMemo(
    () => supplier?.materials ?? [],
    [supplier]
  )

  const invoiceRemaining = invoice === null ? 0 : Math.floor(invoice.purchased_millis / 1000)
  const allocated = Object.values(qtyByMaterial).reduce((a, n) => a + n, 0)
  const headroom = invoiceRemaining - allocated
  const canAccept = invoice !== null && allocated > 0 && headroom >= 0 && !submitting && !loading

  if (material === null) return null
  const isView = material.pending_invoice_count === 0

  const handleSupplier = (value: string): void => {
    setSupplierId(value)
    setManagePurchaseId('')
    setQtyByMaterial({})
  }
  const handleInvoice = (value: string): void => {
    setManagePurchaseId(value)
    setQtyByMaterial({})
  }
  const handleQty = (rawMaterialId: string, qty: number): void => {
    setQtyByMaterial((s) => ({ ...s, [rawMaterialId]: qty }))
  }

  const handleAccept = async (): Promise<void> => {
    if (!canAccept || invoice === null) return
    setSubmitting(true)
    setError(null)
    const lines: ReceiveLine[] = Object.entries(qtyByMaterial)
      .filter(([, q]) => q > 0)
      .map(([raw_material_id, quantity]) => ({ raw_material_id, quantity }))
    const r = await onAllocate({
      managePurchaseId: invoice.purchase_id,
      lines
    })
    setSubmitting(false)
    if (!r.ok) {
      setError(r.message)
      return
    }
    onClose()
  }

  const entryMaterialLabel = material.unit_label !== null
    ? `${material.raw_material_name} (${material.unit_label})`
    : material.raw_material_name

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title={`${isView ? 'View' : 'Receive'} — ${entryMaterialLabel}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {isView ? 'Close' : 'Cancel'}
          </Button>
          {isView ? null : (
            <Button onClick={() => void handleAccept()} disabled={!canAccept}>
              {submitting ? 'Saving…' : 'Accept'}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        {loading ? (
          <div className="flex items-center justify-center py-6"><Spinner /></div>
        ) : (
          <>
            {allSuppliers.length === 0 ? (
              <Alert tone="info">
                No supplier is linked to this material on Manage &gt; Material Suppliers. Add a link there
                first to start receiving stock.
              </Alert>
            ) : eligibleSuppliers.length === 0 ? (
              <Alert tone="info">No invoices for linked supplier.</Alert>
            ) : null}

            <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-body text-body-sm border border-border rounded-md bg-parchment/40 p-3">
              <div><span className="text-text-secondary">Material:</span> <span className="text-charcoal">{material.raw_material_name}</span></div>
              <div><span className="text-text-secondary">Unit:</span> <span className="text-charcoal">{material.unit_label ?? '—'}</span></div>
              <div><span className="text-text-secondary">In stock:</span> <span className="font-mono text-charcoal">{material.in_stock_units} units</span></div>
            </div>

            {eligibleSuppliers.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Supplier" error={null}>
                  <Select
                    value={supplierId}
                    onChange={(e) => handleSupplier(e.target.value)}
                    aria-label="Supplier"
                  >
                    <option value="">— Select a supplier —</option>
                    {eligibleSuppliers.map((s) => (
                      <option key={s.supplier_id} value={s.supplier_id}>
                        {s.supplier_name} ({s.invoices.length} unallocated)
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Invoice" error={null}>
                  <Select
                    value={managePurchaseId}
                    onChange={(e) => handleInvoice(e.target.value)}
                    disabled={supplier === null}
                    aria-label="Invoice"
                  >
                    <option value="">
                      {supplier === null ? 'Pick supplier first' : '— Select an invoice —'}
                    </option>
                    {supplier?.invoices.map((inv) => (
                      <option key={inv.purchase_id} value={inv.purchase_id}>
                        {inv.invoice_no} — {formatQty(inv.purchased_millis)} units
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            ) : null}

            {invoice !== null ? (
              <div className="grid grid-cols-3 gap-x-4 gap-y-1 font-body text-body-sm border border-border rounded-md bg-parchment/40 p-3">
                <div><span className="text-text-secondary">Available to allocate:</span> <span className="font-mono text-charcoal">{invoiceRemaining} units</span></div>
                <div><span className="text-text-secondary">Allocated:</span> <span className="font-mono text-charcoal">{allocated} units</span></div>
                <div><span className="text-text-secondary">Headroom:</span> <span className={`font-mono ${headroom < 0 ? 'text-terra' : 'text-charcoal'}`}>{headroom} units</span></div>
              </div>
            ) : null}

            {supplier !== null && supplierMaterials.length === 0 ? (
              <Alert tone="info">
                This supplier has no linked materials. Add a (supplier, material) row on Manage &gt; Material
                Suppliers first.
              </Alert>
            ) : supplier !== null ? (
              <>
                <p className="font-body text-body-sm text-text-secondary">
                  {invoice === null
                    ? <>Materials linked to this supplier are shown below. Pick an invoice to start allocating.</>
                    : <>Allocate any non-zero amount up to {invoiceRemaining} units across one or more materials. Partial allocations are allowed — you can come back later for the rest.</>}
                </p>
                {invoice === null ? (
                  <MaterialQtyTable
                    materials={supplierMaterials}
                    qtyByMaterial={qtyByMaterial}
                    onQtyChange={handleQty}
                    disabled={true}
                  />
                ) : (
                  <MaterialQtyTable
                    materials={supplierMaterials}
                    qtyByMaterial={qtyByMaterial}
                    onQtyChange={handleQty}
                    remaining={headroom}
                    disabled={submitting}
                  />
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  )
}
